/* Shared landing-page constants, animation variants, and content data */
import {
  Shield, AlertTriangle, Clock, DollarSign, Globe, ShieldCheck, Code2,
  Target, Scan, GitMerge, FileCheck, Box, Cloud, KeyRound, Anchor, Eye,
  Scale, FileCode, Rocket, Layers, Building2,
} from "lucide-react";

/*
 * COLOR SCHEMA: Kosli.com Authentic Palette
 * Light Base: #F7F8FA · Surface: #FFFFFF · Dark: #101C2B · Green: #006D43
 */
export const C = {
  bg: '#F7F8FA',
  surface: '#FFFFFF',
  elevated: '#F1F5F9',
  green: '#006D43',
  greenDim: 'rgba(0,109,67,0.07)',
  greenBorder: 'rgba(0,109,67,0.2)',
  greenDark: '#004126',
  red: '#DC2626',
  redDim: 'rgba(220,38,38,0.06)',
  white: '#FFFFFF',
  text1: '#101C2B',
  text2: '#374151',
  text3: '#4B5563',
  text4: '#6B7280',
  border: '#E5E7EB',
  borderHover: '#CBD5E1',
  // Dark section palette
  dark: '#101C2B',
  darkSurface: '#1B2838',
  darkElevated: '#243044',
  darkBorder: 'rgba(148,163,184,0.15)',
  darkText1: '#F1F5F9',
  darkText2: '#CBD5E1',
  darkText3: '#94A3B8',
  darkText4: '#64748B',
};

export const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.45, ease: [0.25, 0.46, 0.45, 0.94] } })
};
export const stagger = { visible: { transition: { staggerChildren: 0.08 } } };

export const painPoints = [
  { icon: AlertTriangle, title: "Manual Compliance", desc: "Weeks of spreadsheet audits and back-and-forth with security teams." },
  { icon: Clock, title: "Audit Fatigue", desc: "Repetitive evidence collection across multiple frameworks simultaneously." },
  { icon: Shield, title: "Security Gaps", desc: "Misconfigurations slip through to production undetected every sprint." },
  { icon: DollarSign, title: "Cost Overruns", desc: "No visibility into FinOps until the monthly bill arrives as a surprise." },
];

export const platformTabs = [
  { id: "finops", label: "FinOps Auditor", icon: DollarSign, heading: "Real-Time Cost Intelligence", desc: "Itemized resource costing, budget compliance checks, and reserved capacity optimization — all before a single resource deploys.", bullets: ["Resource cost itemization per service", "Budget compliance validation", "Reserved capacity recommendations", "Savings plan optimization"], image: "/screenshots/triple-audit.png" },
  { id: "resilience", label: "Resilience Audit", icon: Globe, heading: "Global Resilience Validation", desc: "Multi-AZ, multi-region validation with auto-scaling simulation and single-point-of-failure elimination.", bullets: ["Multi-AZ/Multi-Region validation", "Auto-scaling & failover simulation", "SPOF detection & elimination", "DR strategy assessment"], image: "/screenshots/architecture-diagram.png" },
  { id: "compliance", label: "Compliance Engine", icon: ShieldCheck, heading: "44+ Framework Compliance Gate", desc: "From HIPAA to SOC 2, PCI-DSS to GDPR — map, enforce, and certify compliance automatically.", bullets: ["44+ compliance frameworks", "Industry preset bundles", "Real-time pass/fail status", "Gap analysis & remediation"], image: "/screenshots/compliance-selector.png" },
  { id: "byoc", label: "BYOC Scanner", icon: Code2, heading: "Bring Your Code — Scan & Fix", desc: "Upload your existing Terraform, CloudFormation, or K8s manifests for instant security scanning with auto-remediation.", bullets: ["Multi-format IaC support", "31 security scanners", "Auto-remediation engine", "Scan history & trends"], image: "/screenshots/byoc-scanner.png" },
];

export const comparisonRows = [
  { task: "Architecture Design", manual: "3-5 days", other: "1-2 days", zerozone: "5 min" },
  { task: "Security Review", manual: "1-2 weeks", other: "2-3 days", zerozone: "3 min" },
  { task: "IaC Generation", manual: "1-2 weeks", other: "1 week", zerozone: "5 min" },
  { task: "Compliance Check", manual: "2-4 weeks", other: "1-2 weeks", zerozone: "2 min" },
  { task: "Total Time", manual: "4-8 weeks", other: "3-4 weeks", zerozone: "~15 min" },
];

export const roiStats = [
  { value: "526%", label: "Average ROI", desc: "Within first year of adoption" },
  { value: "3 mo", label: "Payback Period", desc: "Average time to full return" },
  { value: "200x", label: "Faster Delivery", desc: "Compared to manual process" },
  { value: "44+", label: "Frameworks", desc: "Compliance coverage" },
];

export const testimonials = [
  { quote: "ZeroZone cut our compliance audit prep from 6 weeks to 2 days. The Triple-Audit engine catches things our team missed for months.", name: "Priya Sharma", role: "VP of Engineering, FinScale", avatar: "https://images.unsplash.com/photo-1772987273687-4866382c146f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBoZWFkc2hvdCUyMGF2YXRhciUyMGRhcmslMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc3NTM2MTYzOHww&ixlib=rb-4.1.0&q=85" },
  { quote: "The BYOC scanner found 47 critical misconfigurations in our existing Terraform that we'd deployed to production. Game-changing.", name: "Marcus Chen", role: "Cloud Architect, Nexora Health", avatar: "https://images.unsplash.com/photo-1764545973653-94c40d993495?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBoZWFkc2hvdCUyMGF2YXRhciUyMGRhcmslMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc3NTM2MTYzOHww&ixlib=rb-4.1.0&q=85" },
  { quote: "We went from zero compliance coverage to SOC 2 + HIPAA + PCI-DSS mapped in under an hour. Our auditors were genuinely impressed.", name: "Elena Rodriguez", role: "CISO, DataVault Systems", avatar: "https://images.unsplash.com/photo-1764546373114-2d7a87221733?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBoZWFkc2hvdCUyMGF2YXRhciUyMGRhcmslMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc3NTM2MTYzOHww&ixlib=rb-4.1.0&q=85" },
];

export const paths = [
  { icon: Rocket, title: "Explore", subtitle: "For individual architects", desc: "Visualize, validate, and learn how audit-ready architecture works.", capabilities: ["Architecture visualization", "Preview Resilience Score", "Basic compliance check", "BYOC sample scans", "AWS single-cloud"], cta: "Start Exploring", ctaStyle: "outline" },
  { icon: Layers, title: "Scale", subtitle: "For growing engineering teams", desc: "Full audit suite with multi-cloud support and production IaC export.", capabilities: ["Complete Triple-Audit Suite", "Multi-cloud (AWS, Azure, GCP)", "All IaC export formats", "Compliance framework mapping", "Team collaboration"], cta: "Request Access", ctaStyle: "filled", featured: true },
  { icon: Building2, title: "Govern", subtitle: "For enterprise organizations", desc: "Custom governance, SSO, dedicated support, and unlimited scale.", capabilities: ["Everything in Scale", "SSO/SAML & audit logs", "Custom compliance frameworks", "Unlimited users & projects", "Dedicated account manager"], cta: "Talk to Us", ctaStyle: "outline" },
];

export const pipelineSteps = [
  { num: "01", title: "Define", desc: "Intake requirements through guided wizard or template.", icon: Target },
  { num: "02", title: "Audit", desc: "31 security scanners validate every architecture decision.", icon: Scan },
  { num: "03", title: "Remediate", desc: "Self-healing engine auto-fixes violations before export.", icon: GitMerge },
  { num: "04", title: "Certify", desc: "Export production-ready IaC with compliance attestation.", icon: FileCheck },
];

export const resources = [
  { title: "Shift-Left Security: A CTO's Guide", tag: "Whitepaper", desc: "Why embedding security before deploy is the single highest-ROI decision in 2026." },
  { title: "From SOC 2 Panic to SOC 2 Autopilot", tag: "Case Study", desc: "How FinScale automated compliance mapping for 3 frameworks simultaneously." },
  { title: "The 31-Scanner IaC Pipeline Explained", tag: "Engineering Blog", desc: "Deep dive into Checkov, tfsec, Trivy, Prowler, and 27 more scanners." },
];

export const trustLogos = ["AWS Partner", "SOC 2", "ISO 27001", "HIPAA", "PCI-DSS", "GDPR", "FedRAMP", "NIST"];

export const securityTools = [
  { name: "Checkov", mono: "Ck", desc: "1000+ IaC policies", category: "IaC Scanner" },
  { name: "tfsec", mono: "Tf", desc: "Terraform security", category: "IaC Scanner" },
  { name: "KICS", mono: "Ki", desc: "Multi-IaC scanning", category: "IaC Scanner" },
  { name: "Terrascan", mono: "Ts", desc: "Policy as code", category: "IaC Scanner" },
  { name: "cfn-nag", mono: "Cn", desc: "CloudFormation", category: "IaC Scanner" },
  { name: "cfn-lint", mono: "Cl", desc: "CFN validation", category: "IaC Scanner" },
  { name: "Trivy", mono: "Tv", desc: "CVE detection", category: "Vulnerability" },
  { name: "Grype", mono: "Gr", desc: "SBOM vulnerabilities", category: "Vulnerability" },
  { name: "Snyk", mono: "Sn", desc: "Dependency scanning", category: "Vulnerability" },
  { name: "Anchore", mono: "An", desc: "Container analysis", category: "Container" },
  { name: "Hadolint", mono: "Hd", desc: "Dockerfile linting", category: "Container" },
  { name: "Dockle", mono: "Dk", desc: "Container best practices", category: "Container" },
  { name: "Prowler", mono: "Pw", desc: "300+ AWS checks", category: "Cloud Audit" },
  { name: "ScoutSuite", mono: "Sc", desc: "Multi-cloud audit", category: "Cloud Audit" },
  { name: "CloudSploit", mono: "Cs", desc: "Cloud misconfig", category: "Cloud Audit" },
  { name: "GitLeaks", mono: "Gl", desc: "Secret detection", category: "Secrets" },
  { name: "TruffleHog", mono: "Th", desc: "Credential scanning", category: "Secrets" },
  { name: "detect-secrets", mono: "Ds", desc: "Pre-commit hooks", category: "Secrets" },
  { name: "Kubesec", mono: "Ks", desc: "K8s risk analysis", category: "Kubernetes" },
  { name: "kube-bench", mono: "Kb", desc: "CIS K8s benchmark", category: "Kubernetes" },
  { name: "Polaris", mono: "Pl", desc: "K8s best practices", category: "Kubernetes" },
  { name: "Falco", mono: "Fc", desc: "Runtime security", category: "Runtime" },
  { name: "OPA/Gatekeeper", mono: "OG", desc: "Policy engine", category: "Policy" },
  { name: "Semgrep", mono: "Sg", desc: "Code analysis", category: "SAST" },
];

export const categoryIcons = {
  "IaC Scanner": Shield,
  "Vulnerability": AlertTriangle,
  "Container": Box,
  "Cloud Audit": Cloud,
  "Secrets": KeyRound,
  "Kubernetes": Anchor,
  "Runtime": Eye,
  "Policy": Scale,
  "SAST": FileCode,
};

export const toolCategoryColors = {
  "IaC Scanner":     { bg: 'rgba(37,99,235,0.08)', border: 'rgba(37,99,235,0.2)', text: '#2563EB' },
  "Static Analysis": { bg: 'rgba(37,99,235,0.08)', border: 'rgba(37,99,235,0.2)', text: '#2563EB' },
  "Vulnerability":   { bg: 'rgba(220,38,38,0.08)', border: 'rgba(220,38,38,0.2)', text: '#DC2626' },
  "Container":       { bg: 'rgba(6,182,212,0.08)', border: 'rgba(6,182,212,0.2)', text: '#0891B2' },
  "Cloud Audit":     { bg: 'rgba(202,138,4,0.08)', border: 'rgba(202,138,4,0.2)', text: '#B45309' },
  "Secrets":         { bg: 'rgba(219,39,119,0.08)', border: 'rgba(219,39,119,0.2)', text: '#BE185D' },
  "Kubernetes":      { bg: 'rgba(79,70,229,0.08)', border: 'rgba(79,70,229,0.2)', text: '#4F46E5' },
  "Runtime":         { bg: 'rgba(234,88,12,0.08)', border: 'rgba(234,88,12,0.2)', text: '#C2410C' },
  "Policy":          { bg: 'rgba(124,58,237,0.08)', border: 'rgba(124,58,237,0.2)', text: '#7C3AED' },
  "SAST":            { bg: 'rgba(0,109,67,0.08)', border: 'rgba(0,109,67,0.2)', text: '#006D43' },
};

export const toolLegend = [
  { name: "IaC Scanner", key: "IaC Scanner" }, { name: "Vulnerability", key: "Vulnerability" },
  { name: "Container", key: "Container" }, { name: "Cloud Audit", key: "Cloud Audit" },
  { name: "Secrets", key: "Secrets" }, { name: "Kubernetes", key: "Kubernetes" },
  { name: "SAST", key: "SAST" },
];
